import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  computeUnreadDivider,
  Constants,
  ForwardMessageContext,
  IChatAccess,
  IChatInfoAccess,
  IRouteContext,
  IUnreadSnapshot,
  MessagePanelContext,
  RouteContext,
  UploadProgress,
  useTranslation,
  useUnreadSnapshot,
} from "hhn-commons";
import {
  IDocumentMediaPreviewData,
  IMessage,
  IOrganizationUser,
} from "@home-health-notify/domain";
import MessagePanelItem from "./messagePanelItem/MessagePanelItem";
import { Box } from "@mui/material";
import {
  MassagePanelWrapper,
  MessagePanelItemContainer,
  MessagesContainer,
  ProgressLoaderWrapper,
} from "./MessagePanel.style";
import { DocumentMediaViewerContext } from "../context/DocumentMediaViewerContext";
import GoToBottom from "./GoToBottom";
import UploadProgressBar from "./UploadProgressBar";
import { useSnackbar } from "notistack";
import ProgressLoader from "../loader/ProgressLoader";
import MessagePanelBanner from "./MessagePanelBanner";
import { IChatRoutes } from "../home/IChatRoutes";
import UnreadMessagesDivider from "./UnreadMessagesDivider";
import UnreadMessagesIndicator from "./UnreadMessagesIndicator";

export type IMessageContentTypeBubble = {
  chatInfoAccess?: IChatInfoAccess;
  message: IMessage;
  isSender?: boolean;
  openMediaDialog?: (message: IMessage) => void;
  forcedFallbackContent?: boolean;
  disableActions?: boolean;
  isForwardOn?: boolean;
  isReply?: boolean;
  isReplyPreview?: boolean;
};

export interface IMessageTypeBubble extends IMessageContentTypeBubble {
  children: React.ReactNode;
}

interface MessagePanelProps {
  chatAccess: IChatAccess;
  selectedOrgUser: IOrganizationUser;
  progressData?: UploadProgress;
  isUploadFailed?: boolean;
  scrollMessages: () => Promise<void>;
  isMessageLoading: boolean;
}

export function MessagePanel({
  chatAccess,
  selectedOrgUser,
  progressData,
  isUploadFailed,
  scrollMessages,
  isMessageLoading,
}: MessagePanelProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Map<number, HTMLElement>>(new Map());
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observerLastRef = useRef<IntersectionObserver | null>(null);
  const firstUnreadObserverRef = useRef<IntersectionObserver | null>(null);
  const autoScrolledSnapshotRef = useRef<IUnreadSnapshot | null>(null);
  const dividerRef = useRef<HTMLDivElement | null>(null);

  const {
    setScrollToBottom,
    focusedMessage,
    clearFocusedMessage,
    setFocusedMessage,
    addToSelectedMessage,
  } = useContext(MessagePanelContext);

  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [lastElement, setLastElement] = useState<HTMLElement | null>(null);
  const [dividerElement, setDividerElement] = useState<HTMLElement | null>(
    null,
  );
  const [hasSeenFirstUnread, setHasSeenFirstUnread] = useState<boolean>(false);
  const [isShowGoToBottomButton, setIsShowGoToBottomButton] =
    useState<boolean>(false);
  const { isForwardModeOn } = useContext(ForwardMessageContext);

  const messages = chatAccess.messages;

  const unreadSnapshot = useUnreadSnapshot(
    chatAccess.chatInfo.id,
    messages.messages.length > 0,
    chatAccess.unreadMessageCount?.() ?? 0,
    chatAccess.lastMessageRead?.(),
  );

  const { firstUnreadIndex, hasUnreadAbove } = useMemo(
    () =>
      computeUnreadDivider(
        messages.messages,
        unreadSnapshot?.boundaryMessageId,
        messages.hasNext,
      ),
    [messages.messages, messages.hasNext, unreadSnapshot],
  );

  // The passive chip stays up while there are unread messages above the
  // viewport, until the first unread is actually seen (sticky, so scrolling
  // back down past it doesn't bring it back).
  const showUnreadIndicator = hasUnreadAbove && !hasSeenFirstUnread;

  const { routes } = useContext<IRouteContext<IChatRoutes>>(RouteContext);
  const focusMessageId = routes.getFocusMessageId();

  useEffect(() => {
    if (focusMessageId && messages.messages.length > 0) {
      setFocusedMessage(focusMessageId);
      routes.clearFocusMessageId();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, focusMessageId]);

  const focusOnMessage = (messageId: string) => {
    const messageIndex = messages.messages.findIndex((message: IMessage) => {
      return message.getMessage().id === messageId;
    });

    if (messageIndex === -1) {
      enqueueSnackbar(tNs("scrollUpToViewMessage"), {
        autoHideDuration: 3000,
      });
      clearFocusedMessage();
      return;
    }

    const messageRef = messageRefs.current.get(messageIndex);
    if (messageRef && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messageRef.offsetTop,
        behavior: "smooth",
      });
    }

    setTimeout(() => {
      clearFocusedMessage();
    }, Constants.FOCUS_HIGHLIGHT_DURATION_IN_MS);
  };

  useEffect(() => {
    if (focusedMessage && messages.messages.length > 0) {
      focusOnMessage(focusedMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedMessage, messages]);

  const gotoBottom = () => {
    if (scrollRef && scrollRef.current && scrollRef.current.parentNode) {
      const target = scrollRef.current;
      (target.parentNode as HTMLElement).scrollTop = 0;
    }
  };

  const { openDocumentMediaViewer } = useContext(DocumentMediaViewerContext);

  const openMediaDialog = useCallback(
    (message: IMessage) => {
      const previewMedia = message.getPreviewDataFromMessage();
      const previewMediaList: IDocumentMediaPreviewData[] = messages.mediaList
        .map((message: IMessage) => message.getPreviewDataFromMessage())
        .filter(
          (item): item is IDocumentMediaPreviewData => item !== undefined,
        );
      if (previewMedia) {
        openDocumentMediaViewer(previewMedia, previewMediaList);
      }
    },
    [messages.mediaList, openDocumentMediaViewer],
  );

  const fetchMoreData = async () => {
    await scrollMessages();
  };

  useEffect(() => {
    const observerEleCallback = async (
      entries: IntersectionObserverEntry[],
    ) => {
      const first = entries[0];
      if (first.isIntersecting) {
        await fetchMoreData();
      }
    };
    observerRef.current = new IntersectionObserver(observerEleCallback);
    const currentElement = scrollElement;

    if (currentElement && observerRef.current) {
      observerRef.current.observe(currentElement);
    }

    return () => {
      if (currentElement && observerRef.current) {
        observerRef.current.unobserve(currentElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollElement]);

  useEffect(() => {
    setScrollToBottom(() => () => gotoBottom());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const didReachBottom = () => {
    setIsShowGoToBottomButton(false);
    const lastMessage = messages.messages[0];
    lastMessage.markAsRead();
  };

  const didMoveOffBottom = () => {
    setIsShowGoToBottomButton(true);
  };

  useEffect(() => {
    const observerLastEleCallback = (entries: IntersectionObserverEntry[]) => {
      const first = entries[0];
      if (!first.isIntersecting) {
        didMoveOffBottom();
      } else {
        didReachBottom();
      }
    };

    observerLastRef.current = new IntersectionObserver(observerLastEleCallback);
    const currentElement = lastElement;

    if (currentElement && observerLastRef.current) {
      observerLastRef.current.observe(currentElement);
    }

    return () => {
      if (currentElement && observerLastRef.current) {
        observerLastRef.current.unobserve(currentElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastElement]);

  // A callback ref so the divider node feeds both the auto-scroll (dividerRef,
  // read synchronously) and the observer/anti-flash target (dividerElement).
  const setDividerRef = useCallback((element: HTMLDivElement | null) => {
    dividerRef.current = element;
    setDividerElement(element);
  }, []);

  useEffect(() => {
    setHasSeenFirstUnread(false);
  }, [unreadSnapshot]);

  // On initial open, if the boundary is loaded but scrolled off-screen above,
  // jump to the divider so the user starts at their first unread. Runs once per
  // chat (keyed on the snapshot identity) and only for the initial open — if
  // the boundary isn't loaded yet (large-N), we never auto-scroll, even when a
  // later page brings it in while the user scrolls manually. Since we land away
  // from the bottom, the bottom sentinel won't mark messages read, so we do it
  // explicitly to keep today's "clear unread on open" behavior.
  useLayoutEffect(() => {
    if (!unreadSnapshot || autoScrolledSnapshotRef.current === unreadSnapshot) {
      return;
    }
    autoScrolledSnapshotRef.current = unreadSnapshot;

    const container = messagesContainerRef.current;
    // dividerRef is only attached when the boundary is loaded; when it isn't
    // (large-N) there's nothing to scroll to and we leave the user at the bottom.
    const divider = dividerRef.current;
    if (!container || !divider) {
      return;
    }

    const dividerRect = divider.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const isInView =
      dividerRect.top < containerRect.bottom &&
      dividerRect.bottom > containerRect.top;
    if (!isInView) {
      // scrollIntoView handles the column-reverse scroll axis correctly; the
      // divider's scroll-margin-top keeps it clear of the visibility banner.
      divider.scrollIntoView({ block: "start" });
      messages.messages[0]?.markAsRead();
      setHasSeenFirstUnread(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadSnapshot, firstUnreadIndex]);

  // Before paint: if the divider is already on screen when it mounts (e.g. only
  // 1-2 unread, so it's near the bottom where we land), mark it seen
  // synchronously so the chip never flashes. The observer below handles the
  // case where the user scrolls up to reach it later.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!dividerElement || !container) {
      return;
    }
    const elementRect = dividerElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const isInView =
      elementRect.top < containerRect.bottom &&
      elementRect.bottom > containerRect.top;
    if (isInView) {
      setHasSeenFirstUnread(true);
    }
  }, [dividerElement]);

  // The chip is dismissed only once the divider itself scrolls into view (not
  // merely the first unread message, which sits below the divider).
  useEffect(() => {
    const dividerObserverCallback = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        setHasSeenFirstUnread(true);
      }
    };

    firstUnreadObserverRef.current = new IntersectionObserver(
      dividerObserverCallback,
    );
    const currentElement = dividerElement;

    if (currentElement && firstUnreadObserverRef.current) {
      firstUnreadObserverRef.current.observe(currentElement);
    }

    return () => {
      if (currentElement && firstUnreadObserverRef.current) {
        firstUnreadObserverRef.current.unobserve(currentElement);
      }
    };
  }, [dividerElement]);

  const setRef = (index: number) => (el: HTMLElement | null) => {
    if (index === 0) {
      setLastElement(el ?? null);
    } else if (messages.hasNext && index >= messages.messages.length - 3) {
      setScrollElement(el ?? null);
    }
    if (el) {
      messageRefs.current.set(index, el);
    } else {
      messageRefs.current.delete(index);
    }
  };

  return (
    <MassagePanelWrapper id="scrollableDiv">
      <MessagePanelBanner chatAccess={chatAccess} />
      {showUnreadIndicator && <UnreadMessagesIndicator />}
      {isShowGoToBottomButton && (
        <GoToBottom
          content={chatAccess.unreadMessageCount?.() ?? 0}
          onClick={gotoBottom}
        />
      )}
      <MessagesContainer ref={messagesContainerRef}>
        {messages?.messages.map((message: IMessage, index: number) => {
          const messageId = message.getMessage().id;
          return (
            <React.Fragment key={"message_" + messageId}>
              <MessagePanelItemContainer ref={setRef(index)}>
                <MessagePanelItem
                  message={message}
                  selectedOrgUser={selectedOrgUser}
                  openMediaDialog={openMediaDialog}
                  chatInfoAccess={chatAccess}
                  shouldFocusOnMessage={messageId === focusedMessage}
                  isForwardMessagesOn={isForwardModeOn}
                  disableActions={isForwardModeOn}
                  selectMessage={addToSelectedMessage}
                  hideContextMenu={false}
                  isReplyPreview={false}
                />
              </MessagePanelItemContainer>
              {index === firstUnreadIndex && unreadSnapshot && (
                <UnreadMessagesDivider ref={setDividerRef} />
              )}
            </React.Fragment>
          );
        })}
        {isMessageLoading && (
          <ProgressLoaderWrapper>
            <ProgressLoader />
          </ProgressLoaderWrapper>
        )}
        <Box ref={scrollRef} />
        <UploadProgressBar
          progressData={progressData}
          isUploadFailed={isUploadFailed}
        />
      </MessagesContainer>
    </MassagePanelWrapper>
  );
}
